/** Window-local conversation memory. All composers share the access-revocation boundary. */
const stores = new Set<Map<string, unknown>>();
const cleaners = new Set<(userId: string, allowedChannels?: ReadonlySet<unknown>) => void>();

/** Stores with their own listeners (composer drafts) erase themselves at the same revocation boundary. */
export function onChatMemoryClear(cleaner: (userId: string, allowedChannels?: ReadonlySet<unknown>) => void): void {
  cleaners.add(cleaner);
}
const leases = new Map<string, object>();
export function invalidateChatMemoryLease(key: string): void { leases.delete(key); }

/** Late send callbacks cannot restore memory that was erased while the request was in flight. */
export function chatMemoryLease(key: string): () => boolean {
  const lease = leases.get(key) ?? {};
  leases.set(key, lease);
  return () => leases.get(key) === lease;
}

export function createChatMemory<T>(): Map<string, T> {
  const store = new Map<string, T>();
  stores.add(store);
  return store;
}

/** Keys start with userId:channelId; optional suffixes identify thread composers. */
export function clearChatMemory(userId: string, allowedChannels?: ReadonlySet<unknown>): void {
  for (const cleaner of cleaners) cleaner(userId, allowedChannels);
  for (const key of leases.keys()) {
    if (key.startsWith(`${userId}:`) && !allowedChannels?.has(key.split(':')[1])) leases.delete(key);
  }
  for (const store of stores) {
    for (const key of store.keys()) {
      if (key.startsWith(`${userId}:`) && !allowedChannels?.has(key.split(':')[1])) store.delete(key);
    }
  }
}
